import React, { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

const WORLD_TOPO =
  "https://raw.githubusercontent.com/zcreativelabs/react-simple-maps/master/topojson-maps/world-110m.json";

export default function GlobalMap({ cases, activeCaseId, onSelect }) {
  const [hover, setHover] = useState(null);

  const markers = useMemo(
    () =>
      (cases || [])
        .filter((c) => typeof c.lat === "number" && typeof c.lng === "number")
        .map((c) => ({
          id: c.case_id,
          coordinates: [c.lng, c.lat],
          unresolved: /unresolved/i.test(c.classification || ""),
          agency: c.agency,
          location: c.location,
          incident_date: c.incident_date,
          raw: c,
        })),
    [cases]
  );

  return (
    <div
      data-testid="global-map"
      className="relative h-[420px] bg-zinc-950 border border-zinc-800 scanline overflow-hidden"
    >
      <div className="absolute top-3 left-4 z-10 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        <span className="text-amber-400">●</span> GLOBAL UAP INCIDENT MAP /{" "}
        <span className="text-zinc-300">{markers.length} CONTACTS</span>
      </div>
      <div className="absolute top-3 right-4 z-10 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-3">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse-amber" /> UNRESOLVED
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-400" /> RESOLVED
        </span>
      </div>
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        style={{ width: "100%", height: "100%" }}
      >
        <Geographies geography={WORLD_TOPO}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#18181b"
                stroke="#27272a"
                strokeWidth={0.5}
                style={{
                  default: { outline: "none" },
                  hover: { fill: "#1f1f24", outline: "none" },
                  pressed: { outline: "none" },
                }}
              />
            ))
          }
        </Geographies>
        {markers.map((m) => {
          const isActive = activeCaseId === m.id;
          return (
            <Marker
              key={m.id}
              coordinates={m.coordinates}
              data-testid={`map-marker-${m.id}`}
              onMouseEnter={() => setHover(m)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSelect?.(m.raw)}
              style={{ cursor: "pointer" }}
            >
              <circle
                r={isActive ? 7 : 4}
                fill={m.unresolved ? "#fbbf24" : "#22c55e"}
                fillOpacity={0.25}
              />
              <circle
                r={isActive ? 3.5 : 2.2}
                fill={m.unresolved ? "#fbbf24" : "#22c55e"}
                stroke="#09090b"
                strokeWidth={0.6}
              />
            </Marker>
          );
        })}
      </ComposableMap>

      {hover && (
        <div
          className="absolute bottom-3 left-3 right-3 md:right-auto md:max-w-sm border border-zinc-800 bg-zinc-950/95 px-3 py-2 font-mono text-[11px] text-zinc-300 z-10"
          data-testid="map-tooltip"
        >
          <div className="text-amber-400 font-semibold">{hover.id}</div>
          <div className="text-zinc-500 uppercase tracking-widest text-[10px] mt-0.5">
            {hover.agency} · {hover.incident_date}
          </div>
          <div className="mt-1">{hover.location}</div>
          <div className="mt-1 text-zinc-500">
            [{hover.coordinates[1].toFixed(3)}, {hover.coordinates[0].toFixed(3)}]
          </div>
        </div>
      )}
    </div>
  );
}
